import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Award, TrendingUp, Package, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLASS_COLORS = {
  A: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  B: 'bg-amber-100 text-amber-700 border-amber-300',
  C: 'bg-slate-100 text-slate-600 border-slate-300',
  Z: 'bg-red-100 text-red-700 border-red-300',
};

const CLASS_DESC = {
  A: '80% do lucro',
  B: '15% do lucro',
  C: '5% do lucro',
  Z: 'sem vendas',
};

const PERIODS = [
  { value: 30, label: '30 dias' },
  { value: 60, label: '60 dias' },
  { value: 90, label: '90 dias' },
  { value: 180, label: '6 meses' },
  { value: 365, label: '1 ano' },
];

export const ABCAnalysis = () => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterClass, setFilterClass] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/products/abc-analysis?days=${days}`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const allItems = data ? [
    ...data.items,
    ...data.no_sales,
  ] : [];

  const visibleItems = allItems.filter(it => {
    if (filterClass !== 'all' && it.abc !== filterClass) return false;
    if (search && !it.product_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid="abc-analysis">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider font-bold text-slate-600">Período:</span>
          {PERIODS.map(p => (
            <Button
              key={p.value}
              size="sm"
              variant={days === p.value ? 'default' : 'outline'}
              onClick={() => setDays(p.value)}
              className={days === p.value ? 'bg-emerald-600 hover:bg-emerald-700 h-8' : 'h-8'}
              data-testid={`abc-period-${p.value}`}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-center text-slate-500 p-8">Calculando análise ABC...</p>
      ) : !data ? (
        <p className="text-center text-slate-500 p-8">Sem dados</p>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            {['A', 'B', 'C', 'Z'].map((cls) => (
              <button
                key={cls}
                onClick={() => setFilterClass(filterClass === cls ? 'all' : cls)}
                className={`text-left rounded-md border-2 p-3 transition-all ${CLASS_COLORS[cls]} ${filterClass === cls ? 'ring-2 ring-offset-1 ring-blue-500' : ''}`}
                data-testid={`abc-card-${cls}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider font-bold">Classe {cls}</span>
                  <Award size={14} />
                </div>
                <p className="text-2xl font-black mt-1">{data.counts[cls] || 0}</p>
                <p className="text-[10px] opacity-80">produto(s) · {CLASS_DESC[cls]}</p>
                {cls !== 'Z' && (
                  <p className="text-xs font-bold mt-1">R$ {(data.totals[cls] || 0).toFixed(2)}</p>
                )}
              </button>
            ))}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-md p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider font-bold text-blue-700">Lucro total</span>
                <TrendingUp size={14} className="text-blue-700" />
              </div>
              <p className="text-2xl font-black text-blue-700 mt-1">R$ {data.total_profit.toFixed(2)}</p>
              <p className="text-[10px] text-blue-600">Receita: R$ {data.total_revenue.toFixed(2)}</p>
            </div>
          </div>

          {/* Filtros */}
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Button size="sm" variant={filterClass === 'all' ? 'default' : 'outline'} onClick={() => setFilterClass('all')} className={filterClass === 'all' ? 'bg-blue-600 hover:bg-blue-700 h-8' : 'h-8'}>
                Todos ({allItems.length})
              </Button>
              {['A', 'B', 'C', 'Z'].map(cls => (
                <Button key={cls} size="sm" variant={filterClass === cls ? 'default' : 'outline'} onClick={() => setFilterClass(cls)} className={`h-8 ${filterClass === cls ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}>
                  {cls} ({data.counts[cls] || 0})
                </Button>
              ))}
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrar produto..." className="pl-9 h-8" data-testid="abc-search" />
            </div>
          </div>

          {/* Tabela */}
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0 text-xs uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="text-left p-2 w-16">Classe</th>
                    <th className="text-left p-2">Produto</th>
                    <th className="text-left p-2">Categoria</th>
                    <th className="text-right p-2">Qtd vendida</th>
                    <th className="text-right p-2">Receita</th>
                    <th className="text-right p-2">Custo</th>
                    <th className="text-right p-2">Lucro</th>
                    <th className="text-right p-2">Margem</th>
                    <th className="text-right p-2">% acumul.</th>
                    <th className="text-right p-2">Estoque</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visibleItems.length === 0 ? (
                    <tr><td colSpan={10} className="p-6 text-center text-slate-400">Nenhum item para os filtros selecionados</td></tr>
                  ) : visibleItems.map(it => (
                    <tr key={it.product_id} className="hover:bg-slate-50" data-testid={`abc-row-${it.product_id}`}>
                      <td className="p-2">
                        <span className={`inline-flex w-8 h-8 items-center justify-center rounded-full font-black text-sm border-2 ${CLASS_COLORS[it.abc]}`}>
                          {it.abc}
                        </span>
                      </td>
                      <td className="p-2 font-medium">{it.product_name}</td>
                      <td className="p-2 text-xs text-slate-600 capitalize">{(it.category || '').replace('_', ' ')}</td>
                      <td className="p-2 text-right">{it.quantity != null ? `${it.quantity} ${it.unit}` : '—'}</td>
                      <td className="p-2 text-right">{it.revenue != null ? `R$ ${it.revenue.toFixed(2)}` : '—'}</td>
                      <td className="p-2 text-right text-slate-600">{it.cost != null ? `R$ ${it.cost.toFixed(2)}` : '—'}</td>
                      <td className="p-2 text-right font-bold text-emerald-700">{it.profit != null ? `R$ ${it.profit.toFixed(2)}` : '—'}</td>
                      <td className="p-2 text-right">{it.margin_percent != null ? `${it.margin_percent.toFixed(1)}%` : '—'}</td>
                      <td className="p-2 text-right text-xs text-slate-500">{it.cumulative_percent != null ? `${it.cumulative_percent.toFixed(1)}%` : '—'}</td>
                      <td className="p-2 text-right text-xs">
                        <span className="inline-flex items-center gap-1">
                          <Package size={11} /> {it.current_stock}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-3">
            <strong>Dica:</strong> Foque promoções em produtos <strong className="text-emerald-700">A</strong>, controle estoque dos <strong className="text-amber-700">B</strong>, reveja preço/giro dos <strong className="text-slate-700">C</strong> e avalie descontinuar produtos <strong className="text-red-700">Z</strong> (sem venda).
          </p>
        </>
      )}
    </div>
  );
};

export default ABCAnalysis;
